import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginUser, logoutUser, getCurrentUser, refreshAccessToken } from '../api/authService';
import { getToken, setToken, clearToken } from '../api/tokenStore.js';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user on mount — use the httpOnly refresh cookie to restore session
  useEffect(() => {
    // Cancellation flag: prevents stale async callbacks from updating state
    // after React Strict Mode unmounts and remounts this effect.
    let cancelled = false;

    const loadUser = async () => {
      try {
        const existingToken = getToken();

        if (existingToken) {
          setTokenState(existingToken);
          const { data } = await getCurrentUser();
          if (cancelled) return;
          setUser(data.user);
          return;
        }

        // Use the refresh endpoint to get a new access token from the httpOnly cookie
        const { data: refreshData } = await refreshAccessToken();
        if (cancelled) return;
        if (refreshData?.accessToken) {
          setToken(refreshData.accessToken);
          setTokenState(refreshData.accessToken);
          const { data } = await getCurrentUser();
          if (cancelled) return;
          setUser(data.user);
        }
      } catch (error) {
        if (cancelled) return;
        // No valid session — clear any stale state
        clearToken();
        setTokenState(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadUser();

    return () => {
      cancelled = true;
    };
  }, []);

  // Login action — identifier is email or phone number
  const loginAction = useCallback(async (identifier, password) => {
    try {
      const { data } = await loginUser({ identifier, password });

      setUser(data.user);
      setToken(data.accessToken); // available after same-tab refresh via sessionStorage
      setTokenState(data.accessToken);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
      };
    }
  }, []);

  // Logout action
  const logoutAction = useCallback(async () => {
    try {
      // Mark user offline in presence store first (best-effort, non-blocking on failure).
      const { sendOffline } = await import('../api/presenceService');
      await sendOffline().catch(() => {});
      await logoutUser();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setTokenState(null);
      clearToken();
    }
  }, []);

  // Check if user has a specific role
  const hasRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  // Check if user has access to a specific page
  const canAccess = useCallback((page) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin has access to everything
    return user.pageAccess?.[page] === true;
  }, [user]);

  // Convenience role checks
  const isAdmin = user?.role === 'admin';
  const isAnalyst = user?.role === 'analyst' || user?.role === 'admin';
  const isViewer = user?.role === 'viewer';

  const value = {
    user,
    token,
    loading,
    loginAction,
    logoutAction,
    hasRole,
    canAccess,
    isAdmin,
    isAnalyst,
    isViewer,
    setUser, // For updating user after profile changes
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
